/**
 * saleTicketUltraCompactText.ts — Builder de texto plano ULTRA COMPACTO.
 *
 * Diseño: 1 línea por producto, SIN rejillas ni separadores entre ítems, y
 * detalle resumido SOLO cuando existe (variantes/modificadores/impuestos/
 * opciones de menú) en una línea `·` indentada.
 *
 *   80mm (48 col):
 *     #I CANT DESCRIP                   VALOR
 *      1   1  Billetera Hombre Cuero.   $95.000
 *          SKU: ACC-002 · P.UNIT: $84.034 · Color: Beige
 *
 *   58mm (32 col): mismas reglas con menos columnas (sin P.UNIT en la fila).
 *
 * Por qué es ULTRA y no compacto:
 *  - Productos simples ocupan UNA línea (compacto reserva una segunda).
 *  - No hay rejillas, ni reglas entre productos, ni líneas vacías.
 *  - El detalle es opcional y se agrupa con " · " (una sola línea envuelta).
 *
 * REGLA DE SEGURIDAD: solo ventas estándar (mismo gate que compacto).
 */

import type { SaleTicketEscPosInput, BusinessInfo } from "./saleTicketEscPos";
import {
  cartItemsToTicketLines,
  TICKET_BASE_PRICE_LABEL,
} from "./ticketLineBreakdown";
import { appendTaxLinesToTicket } from "./cartDiscountUtils";
import {
  getTicketMaxWidth,
  sanitizeForThermal,
  wrapThermalText,
} from "./escposLayout";
import {
  blockHeaderTotal,
  groupTicketItemBlocks,
  isAddonsGroupBlock,
  isMenuGroupBlock,
  splitTicketVariantSummary,
  ticketSubLineLineTotal,
} from "./ticketItemBlocks";
import type { TicketItem } from "@/features/caja/components/TicketItemsTable";
import {
  buildGlobalDetailHeaderLines,
  buildGlobalFooterLines,
  buildGlobalHeaderLines,
  buildGlobalNotaLines,
  buildGlobalPaymentLines,
  buildGlobalTotalsLines,
} from "./saleTicketGlobalSections";

/** Dinero COP local (mismo formato que el resto de builders). */
function money(n: number): string {
  return `$${Math.round(n).toLocaleString("es-CO")}`;
}

/** Trunca a `w` caracteres (sin cortar palabras) agregando "." si se cortó. */
function truncateText(text: string, w: number): string {
  const t = (text || "").replace(/\s+/g, " ").trim();
  if (t.length <= w) return t;
  if (w <= 1) return t.slice(0, w);
  const cut = t.slice(0, w);
  const lastSpace = cut.lastIndexOf(" ");
  const clean = lastSpace >= 8 ? cut.slice(0, lastSpace) : cut;
  return clean.slice(0, Math.max(1, w - 1)) + ".";
}

function cellLeft(text: string, w: number): string {
  return truncateText(text, w).padEnd(w, " ");
}

function cellRight(text: string, w: number): string {
  const t = (text || "").trim();
  if (t.length > w) return truncateText(t, w);
  return t.padStart(w, " ");
}

/** Layout de la fila principal (80=48 col / 58=32 col). */
function rowCols(
  width: number,
  idxW: number,
): {
  idxW: number;
  qtyW: number;
  nameW: number;
  valW: number;
} {
  const qtyW = 4;
  // El monto JAMÁS se trunca: 12 cols en 80mm (cabe $999.999.999) y 11 en 58mm
  // (cabe $99.999.999) — los precios de 7+ cifras ($10.490.000) entran.
  const valW = width <= 32 ? 11 : 12;
  // 4 columnas (idx, cant, descrip, valor) → 3 separadores.
  const nameW = Math.max(10, width - (idxW + 1 + qtyW + 1 + valW + 1));
  return { idxW, qtyW, nameW, valW };
}

/** Cabecera minimalista del detalle ultra. */
function pushUltraHeader(lines: string[], width: number, idxW: number): void {
  const c = rowCols(width, idxW);
  lines.push(
    sanitizeForThermal(
      `${cellLeft("#I", c.idxW)} ${cellLeft("CANT", c.qtyW)} ${cellLeft("DESCRIP", c.nameW)} ${cellRight("VALOR", c.valW)}`,
    ),
  );
}

/** Fila principal de producto: `# CANT DESCRIP… VALOR` (una sola línea). */
function pushUltraRow(
  lines: string[],
  width: number,
  index: number,
  qty: string,
  nombre: string,
  total: string,
  idxW: number,
): void {
  const c = rowCols(width, idxW);
  lines.push(
    sanitizeForThermal(
      `${cellRight(String(index), c.idxW)} ${cellLeft(qty, c.qtyW)} ${cellLeft(nombre, c.nameW)} ${cellRight(total, c.valW)}`,
    ),
  );
}

/** Detalle opcional, indentado bajo la descripción (wrap si hace falta). */
function pushUltraDetail(lines: string[], text: string, width: number): void {
  const body = (text || "").replace(/\s+/g, " ").trim();
  if (!body) return;
  const indent = 8;
  const bodyW = Math.max(10, width - indent);
  const wrapped = body.length <= bodyW ? [body] : wrapThermalText(body, bodyW);
  for (const ln of wrapped) {
    lines.push(
      sanitizeForThermal(`${" ".repeat(indent)}${ln}`.padEnd(width, " ")),
    );
  }
}

/** Cantidad visible (unidades o kg). */
function qtyOf(header: TicketItem): { cell: string; note: string } {
  const isPeso = !!header.peso && header.peso > 0;
  if (!isPeso) {
    return { cell: String(header.cantidad ?? 1), note: "" };
  }
  const kg = header.peso ?? 0;
  const entero = Number.isInteger(kg);
  const cell = entero ? `${kg} KG` : "KG";
  const valor = entero ? "" : kg.toFixed(2).replace(".", ",").replace(/0$/, "");
  return { cell, note: valor ? `Peso: ${valor} kg` : "" };
}

/** Construye las filas de ticket (misma tubería que el resto de builders). */
function buildTicketLines(
  input: SaleTicketEscPosInput,
  defaultRatePct = 0,
): TicketItem[] {
  const opts = input.catalogProducts?.length
    ? { catalog: input.catalogProducts }
    : undefined;
  const base = cartItemsToTicketLines(input.cart, opts);
  // Sin descuentos por línea en el detalle: viven una sola vez en Totales.
  return appendTaxLinesToTicket(base, input.cart, defaultRatePct);
}

/**
 * Emite un bloque de ticket en formato ultra: fila principal (1 línea) y
 * detalle `·` opcional (solo si el ítem lo necesita).
 */
function emitUltraBlock(
  lines: string[],
  block: TicketItem[],
  width: number,
  index: number,
  idxW: number,
): void {
  const header = block[0];
  if (!header) return;

  if (header.isDiscountLine || header.isArticleTotalLine || header.isTaxLine) {
    // Impuesto/descuento suelto (sin producto): detalle sin número.
    for (const row of block) {
      const monto = Math.round(row.lineSubtotal ?? 0);
      pushUltraDetail(
        lines,
        `${row.isDiscountLine ? "-" : ""}${row.nombre}: ${monto < 0 ? "-" : ""}${money(Math.abs(monto))}`,
        width,
      );
    }
    return;
  }

  const isAddons = isAddonsGroupBlock(block);
  const isMenu = !!header.isGroupHeader && isMenuGroupBlock(block);
  // Los hubs de adiciones llevan cantidad 1 (igual que hubs de menú).
  const qty = qtyOf(header);
  const total = blockHeaderTotal(header);

  // 1) Fila principal (el total de un hub de adiciones va en sus líneas).
  pushUltraRow(
    lines,
    width,
    index,
    qty.cell,
    header.nombre || "Adiciones",
    isAddons && !isMenu ? "" : money(total),
    idxW,
  );

  // 2) Detalle resumido SOLO si existe (agrupado con " · "). En ultra el SKU
  // y P.UNIT NO se repiten (van implícitos en la fila): solo peso fraccional,
  // variantes, impuestos/descuentos, adicionales y opciones de menú.
  const detail: string[] = [];
  if (qty.note) detail.push(qty.note);

  // Variantes del encabezado (ej. Color/Talla).
  for (const v of splitTicketVariantSummary(header.variantesResumen ?? "")) {
    detail.push(v);
  }

  // Sublíneas: descuentos/adiciones/opciones sin costo. En ultra el IVA por
  // línea se omite (ya va en el total global) salvo que el ítem tenga
  // descuento, para explicar el neto de esa fila (como en el mock).
  const priced: string[] = [];
  const free: string[] = [];
  const hasItemDiscount = block.some((it) => it.isDiscountLine);
  for (const sub of block.slice(1)) {
    if (sub.isGroupFooter || sub.isGroupHeader) continue;
    const subName = (sub.nombre ?? "").trim();
    // "Precio Base"/total de artículo ya van en la fila principal: no repetir.
    if (subName === TICKET_BASE_PRICE_LABEL || sub.isArticleTotalLine) {
      continue;
    }
    // IVA/impuesto de línea: solo si hay descuento en el mismo ítem.
    if (sub.isTaxLine && !hasItemDiscount) continue;
    // Mismo cálculo de monto que compacto/rejilla (fuente única).
    const monto = Math.round(ticketSubLineLineTotal(sub));
    const pricedKind = sub.isDiscountLine || sub.isTaxLine;
    if (sub.isSubLine || pricedKind) {
      if (monto !== 0 || pricedKind) {
        // Descuento → "-"; adicional opcional del menú (monto>0) → "+";
        // impuesto/IVA → sin signo (ya está incluido en el total).
        const signo =
          sub.isDiscountLine || monto < 0 ? "-" : sub.isTaxLine ? "" : "+";
        priced.push(`${subName}: ${signo}${money(Math.abs(monto))}`);
      } else if (subName) {
        free.push(subName);
      }
      continue;
    }
    if (subName) free.push(subName);
  }

  // Opciones del footer de grupo (menú/adiciones) agrupadas en una línea.
  const footerText = isFooterText(block);

  if (detail.length) pushUltraDetail(lines, detail.join(" · "), width);
  if (free.length && !footerText) {
    pushUltraDetail(lines, free.join(" · "), width);
  }
  for (const p of priced) pushUltraDetail(lines, p, width);
  if (footerText) pushUltraDetail(lines, `> ${footerText}`, width);
}

/** Opciones del footer de grupo (menú/adiciones) agrupadas en una línea. */
function isFooterText(block: TicketItem[]): string {
  const footer = block.find((it) => it.isGroupFooter);
  return (footer?.opcionesResumen ?? footer?.variantesResumen)?.trim() ?? "";
}

/**
 * Líneas de texto plano del ticket ULTRA COMPACTO (58/80 mm).
 */
export function buildSaleTicketUltraCompactPlainTextLines(
  input: SaleTicketEscPosInput,
  biz: BusinessInfo,
): string[] {
  const lines: string[] = [];
  const maxW = getTicketMaxWidth(input.paperWidthMm);

  lines.push(...buildGlobalHeaderLines(input, biz));
  lines.push(...buildGlobalDetailHeaderLines(input));

  const ticketLines = buildTicketLines(input, biz.impuestoDefaultPct ?? 0);
  const blocks = groupTicketItemBlocks(ticketLines);
  const productCount = blocks.filter((b) => {
    const f = b[0];
    return Boolean(
      f && !f.isDiscountLine && !f.isArticleTotalLine && !f.isTaxLine,
    );
  }).length;
  const idxW = Math.max(2, Math.min(3, String(productCount).length));
  pushUltraHeader(lines, maxW, idxW);

  let index = 0;
  for (const block of blocks) {
    const first = block[0];
    if (!first) continue;
    if (first.isDiscountLine || first.isArticleTotalLine || first.isTaxLine) {
      emitUltraBlock(lines, block, maxW, 0, idxW);
      continue;
    }
    index += 1;
    emitUltraBlock(lines, block, maxW, index, idxW);
  }

  lines.push(...buildGlobalTotalsLines(input));
  lines.push(...buildGlobalPaymentLines(input));
  lines.push(...buildGlobalNotaLines(input));
  lines.push("-".repeat(maxW));

  lines.push(...buildGlobalFooterLines(biz, maxW));
  lines.push("");
  lines.push("");

  return lines;
}
